import React from 'react';
import { motion } from 'framer-motion';
import { db } from '../../firebase';
import { collection, addDoc, query, where, onSnapshot } from 'firebase/firestore';
import { generateTimeSlots, isSlotBooked } from '../../utils/appointmentUtils';

const BookAppointment = () => {
    const [formData, setFormData] = React.useState({
        name: '',
        phone: '',
        email: '',
        city: '',
        serviceType: '',
        date: '',
        timeSlot: '',
        message: ''
    });

    const [bookedSlots, setBookedSlots] = React.useState({});
    const [, setLoading] = React.useState(false);

    // Fetch bookings from Firestore
    React.useEffect(() => {
        // Create a listener for real-time updates
        const q = query(
            collection(db, "appointments"),
            where("date", ">=", new Date().toISOString().split('T')[0])
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const bookings = {};
            snapshot.docs.forEach(doc => {
                const data = doc.data();
                // Create a unique key for each booked slot: date_service_time
                const key = `${data.date}_${data.serviceType}_${data.timeSlot}`;
                bookings[key] = true;
            });
            setBookedSlots(bookings);
        }, (error) => {
            console.error("Error fetching appointments: ", error);
        });

        return () => unsubscribe();
    }, []);

    const timeSlots = generateTimeSlots(formData.serviceType);

    const handleChange = (e) => {
        const { name, value } = e.target;

        // Phone number validation: allow only numbers and max 10 digits
        if (name === 'phone') {
            const numericValue = value.replace(/\D/g, ''); // Remove non-numeric chars
            if (numericValue.length <= 10) {
                setFormData({ ...formData, [name]: numericValue });
            }
            return;
        }

        // Reset time slot only when service type changes
        if (name === 'serviceType') {
            setFormData({ ...formData, [name]: value, timeSlot: '' });
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const { name, phone, email, city, serviceType, date, timeSlot, message } = formData;

        // Phone number validation
        if (phone.length !== 10) {
            alert('⚠️ Please enter a valid 10-digit phone number.');
            return;
        }

        // Check if time slot is selected
        if (!timeSlot) {
            alert('⚠️ Please select a time slot for your appointment.');
            return;
        }

        if (isSlotBooked(date, serviceType, timeSlot, bookedSlots)) {
            alert('❌ This time slot has just been booked. Please select another slot.');
            return;
        }

        setLoading(true);

        try {
            // 1. Save to Firestore
            await addDoc(collection(db, "appointments"), {
                name,
                phone,
                email,
                city,
                serviceType,
                date,
                timeSlot,
                message: message || '',
                createdAt: new Date().toISOString()
            });

            // 2. Prepare Email and WhatsApp Content
            const selectedSlot = timeSlots.find(slot => slot.value === timeSlot);
            const formattedDate = new Date(date).toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
            const finalTimeSlot = selectedSlot?.display || timeSlot;

            // 3. Send Email via FormSubmit (Better for table format automatically)
            await fetch("https://formsubmit.co/ajax/info@nyssahealth.com", {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({
                    _subject: `New Appointment Request - ${name}`,
                    _template: 'table', // FormSubmit uses a table template if specified
                    "Patient Name": name,
                    "Phone Number": phone,
                    "Email Address": email || 'Not provided',
                    "City": city || 'Not specified',
                    "Service Type": serviceType,
                    "Appointment Date": formattedDate,
                    "Time Slot": finalTimeSlot,
                    "Message": message || 'No message provided'
                })
            });

            // 4. Send WhatsApp
            const whatsappMessage = `NYSSA Heal Within - New Appointment Request\n` +
                `━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n` +
                ` *Patient Details*\n` +
                ` Name: ${name}\n` +
                ` Phone: ${phone}\n` +
                ` Email: ${email}\n` +
                ` City: ${city || 'Not specified'}\n\n` +
                ` *Appointment Information*\n` +
                ` Service Type: ${serviceType}\n` +
                ` Date: ${formattedDate}\n` +
                ` Time Slot: ${finalTimeSlot}\n\n` +
                ` *Message*\n${message || 'No message provided'}\n\n` +
                `━━━━━━━━━━━━━━━━━━━━━━━━━━\n` +
                `_Submitted via Nyssa Heal Within Website_`;

            const whatsappUrl = `https://wa.me/917070705590?text=${encodeURIComponent(whatsappMessage)}`;
            window.open(whatsappUrl, '_blank');

            alert('Appointment booked successfully! ✅');

            setFormData({
                name: '',
                phone: '',
                email: '',
                city: '',
                serviceType: '',
                date: '',
                timeSlot: '',
                message: ''
            });

        } catch (error) {
            console.error("Error booking appointment: ", error);
            alert('❌ Error booking appointment. Please try again.');
        } finally {
            setLoading(false);
        }
    };


    const inputStyle = {
        width: '100%',
        padding: '15px',
        border: '1px solid #eee',
        borderRadius: '8px',
        outline: 'none',
        fontSize: '0.9rem',
        color: 'var(--text-main)',
        backgroundColor: '#fbfbfb',
        transition: 'border-color 0.3s ease',
        boxSizing: 'border-box'
    };

    const focusStyle = (e) => {
        e.target.style.borderColor = 'var(--primary)';
    };

    const blurStyle = (e) => {
        e.target.style.borderColor = '#eee';
    };

    // Get minimum date (today)
    const getMinDate = () => {
        const today = new Date();
        return today.toISOString().split('T')[0];
    };

    return (
        <section className="section" id="book-appointment" style={{
            backgroundImage: 'url("https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed',
            padding: '100px 0',
            position: 'relative'
        }}>
            {/* Dark Overlay */}
            <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.3)'
            }}></div>

            <div className="container" style={{ position: 'relative', zIndex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <motion.div
                        initial={{ opacity: 0, x: 50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="appointment-card"
                        style={{
                            backgroundColor: 'var(--white)',
                            padding: '60px',
                            maxWidth: '600px',
                            width: '100%',
                            boxShadow: '0 20px 50px rgba(0,0,0,0.1)'
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                            <span style={{
                                fontFamily: 'cursive',
                                color: 'var(--primary)',
                                fontSize: '2rem',
                                marginRight: '15px'
                            }}>The</span>
                            <h3 style={{
                                fontSize: '1.8rem',
                                fontFamily: 'var(--font-heading)',
                                color: 'var(--secondary)',
                                margin: 0
                            }}>Nyssa Clinic</h3>
                        </div>

                        <h2 style={{
                            fontSize: '2.5rem',
                            fontFamily: 'var(--font-heading)',
                            color: 'var(--secondary)',
                            marginBottom: '30px',
                            lineHeight: '1.2'
                        }}>
                            Share your concern here and begin to heal within
                        </h2>

                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '30px' }}>
                            <div style={{ width: '50px', height: '1px', backgroundColor: 'var(--primary)', marginRight: '15px' }}></div>
                            <span style={{ textTransform: 'uppercase', fontSize: '0.9rem', color: 'var(--primary)', letterSpacing: '1px' }}>book an appointment</span>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className="appointment-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                                <input
                                    type="text"
                                    name="name"
                                    placeholder="Name*"
                                    style={inputStyle}
                                    onFocus={focusStyle}
                                    onBlur={blurStyle}
                                    value={formData.name}
                                    onChange={handleChange}
                                    required
                                />
                                <input
                                    type="tel"
                                    name="phone"
                                    placeholder="Phone*"
                                    style={inputStyle}
                                    onFocus={focusStyle}
                                    onBlur={blurStyle}
                                    value={formData.phone}
                                    onChange={handleChange}
                                    required
                                />
                                <input
                                    type="email"
                                    name="email"
                                    placeholder="E-mail"
                                    style={inputStyle}
                                    onFocus={focusStyle}
                                    onBlur={blurStyle}
                                    value={formData.email}
                                    onChange={handleChange}
                                />
                                <input
                                    type="text"
                                    name="city"
                                    placeholder="City*"
                                    style={inputStyle}
                                    onFocus={focusStyle}
                                    onBlur={blurStyle}
                                    value={formData.city}
                                    onChange={handleChange}
                                    required
                                />
                                <select
                                    name="serviceType"
                                    style={inputStyle}
                                    onFocus={focusStyle}
                                    onBlur={blurStyle}
                                    value={formData.serviceType}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="" disabled>Select Appointment Type</option>
                                    <option value="Cosmetic Gynaecology">Cosmetic Gynaecology</option>
                                    <option value="Cosmetic Dermatology">Cosmetic Dermatology</option>
                                </select>
                                <input
                                    type={formData.date ? "date" : "text"}
                                    name="date"
                                    placeholder="Date*"
                                    style={inputStyle}
                                    onFocus={(e) => {
                                        e.target.type = 'date';
                                        focusStyle(e);
                                        // Attempt to show picker on focus as well
                                        if (e.target.showPicker) {
                                            try { e.target.showPicker(); } catch (err) { console.log(err); }
                                        }
                                    }}
                                    onClick={(e) => {
                                        if (e.target.showPicker) {
                                            try { e.target.showPicker(); } catch (err) { console.log(err); }
                                        }
                                    }}
                                    onBlur={(e) => {
                                        if (!formData.date) e.target.type = 'text';
                                        blurStyle(e);
                                    }}
                                    value={formData.date}
                                    onChange={handleChange}
                                    min={getMinDate()}
                                    required
                                />
                            </div>

                            {/* Time Slot Selection */}
                            {formData.serviceType && formData.date && (
                                <div style={{ marginBottom: '20px' }}>
                                    <label style={{
                                        display: 'block',
                                        marginBottom: '10px',
                                        fontSize: '0.9rem',
                                        color: 'var(--secondary)',
                                        fontWeight: '600'
                                    }}>
                                        Select Time Slot*
                                    </label>
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                                        gap: '10px',
                                        maxHeight: '300px',
                                        overflowY: 'auto',
                                        padding: '10px',
                                        border: '1px solid #eee',
                                        borderRadius: '5px',
                                        backgroundColor: '#fbfbfb'
                                    }}>
                                        {timeSlots.map((slot) => {
                                            const isBooked = isSlotBooked(formData.date, formData.serviceType, slot.value, bookedSlots);
                                            const isSelected = formData.timeSlot === slot.value;

                                            return (
                                                <button
                                                    key={slot.value}
                                                    type="button"
                                                    onClick={() => !isBooked && setFormData({ ...formData, timeSlot: slot.value })}
                                                    disabled={isBooked}
                                                    style={{
                                                        padding: '10px',
                                                        border: isSelected ? '2px solid var(--primary)' : '1px solid #ddd',
                                                        borderRadius: '5px',
                                                        backgroundColor: isBooked ? '#ffebee' : (isSelected ? '#e8f5e9' : '#f0fdf4'),
                                                        color: isBooked ? '#c62828' : (isSelected ? '#2e7d32' : '#1b5e20'),
                                                        cursor: isBooked ? 'not-allowed' : 'pointer',
                                                        fontSize: '0.75rem',
                                                        fontWeight: isSelected ? '600' : '400',
                                                        transition: 'all 0.3s ease',
                                                        opacity: isBooked ? 0.6 : 1
                                                    }}
                                                    onMouseOver={(e) => {
                                                        if (!isBooked && !isSelected) {
                                                            e.target.style.backgroundColor = '#dcfce7';
                                                            e.target.style.borderColor = 'var(--primary)';
                                                        }
                                                    }}
                                                    onMouseOut={(e) => {
                                                        if (!isBooked && !isSelected) {
                                                            e.target.style.backgroundColor = '#f0fdf4';
                                                            e.target.style.borderColor = '#ddd';
                                                        }
                                                    }}
                                                >
                                                    {slot.display}
                                                    {isBooked && <div style={{ fontSize: '0.65rem', marginTop: '2px' }}> Booked</div>}
                                                    {!isBooked && <div style={{ fontSize: '0.65rem', marginTop: '2px' }}> Available</div>}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Helper message when service type or date not selected */}
                            {/* {(!formData.serviceType || !formData.date) && (
                                <div style={{
                                    marginBottom: '20px',
                                    padding: '15px',
                                    backgroundColor: '#fff3cd',
                                    border: '1px solid #ffc107',
                                    borderRadius: '5px',
                                    fontSize: '0.85rem',
                                    color: '#856404'
                                }}>
                                    ℹ️ Please select both <strong>Appointment Type</strong> and <strong>Date</strong> to view available time slots.
                                </div>
                            )} */}

                            <textarea
                                name="message"
                                placeholder="Nyssa se kaho"
                                rows="4"
                                style={{ ...inputStyle, width: '100%', marginBottom: '30px', resize: 'none' }}
                                onFocus={focusStyle}
                                onBlur={blurStyle}
                                value={formData.message}
                                onChange={handleChange}
                            ></textarea>

                            <button type="submit" style={{
                                backgroundColor: 'transparent',
                                border: '1px solid var(--secondary)',
                                color: 'var(--secondary)',
                                padding: '15px 40px',
                                textTransform: 'uppercase',
                                letterSpacing: '2px',
                                fontSize: '0.9rem',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease'
                            }}
                                onMouseOver={(e) => { e.target.style.backgroundColor = 'var(--secondary)'; e.target.style.color = 'var(--white)'; }}
                                onMouseOut={(e) => { e.target.style.backgroundColor = 'transparent'; e.target.style.color = 'var(--secondary)'; }}
                            >
                                Submit Form
                            </button>
                        </form>
                    </motion.div>
                </div>
            </div>

            {/* Mobile Responsive Styles */}
            <style>{`
            @media (max-width: 768px) {
                section[style*="padding: 100px 0"] {
                    padding: 60px 0 !important;
                    background-attachment: scroll !important;
                }

                section div[style*="display: flex"][style*="justifyContent: flex-end"] {
                    justify-content: center !important;
                }

                .appointment-card {
                    padding: 30px !important;
                    max-width: 100% !important;
                }

                .appointment-card h2 {
                    font-size: 1.8rem !important;
                }

                .appointment-card h3 {
                    font-size: 1.5rem !important;
                }

                .appointment-form-grid {
                    grid-template-columns: 1fr !important;
                }

                section input,
                section select,
                section textarea {
                    font-size: 16px !important;
                }

                section button {
                    width: 100% !important;
                }
            }
        `}</style>
        </section>
    );
};

export default BookAppointment;
